import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Sparkle, FloppyDisk, PaperPlaneTilt, Paperclip, Trash, CheckCircle, FilePdf, Image as ImageIcon, File as FileIcon,
  Printer, Wrench, Translate, ArrowsClockwise, CaretDown, DotsThree,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth, formatApiErrorDetail } from '@/auth/AuthContext';
import { engApi, REPORT_DOC_LABEL, REPORT_DOC_BADGE } from './engApi';
import EngProjectTitlePicker from './components/EngProjectTitlePicker';
import EngWorkTargetDialog, { projectStatusBadge } from './components/EngWorkTargetDialog';
import EngReportAiPanel, { AiProgressBar } from './components/EngReportAiPanel';
import { openPrintWindow } from '@/lib/printWindow';
import { buildReportPrintHtml } from './engReportPrint';
import LinkedDocsPanel from '@/pages/docs/LinkedDocsPanel';
import TraceabilityPanel from '@/pages/trace/TraceabilityPanel';
import { dmsApi } from '@/pages/docs/dmsApi';
import { formatTime } from '@/utils/datetime';

const FIELDS = [
  { key: 'objective', label: '本日の目標', q: '今日のゴールは何でしたか？', required: true },
  { key: 'actions', label: '実施した作業', q: '今日はどんな作業を行いましたか？', required: true },
  { key: 'problem', label: '課題・リスク', q: 'どんな問題・リスクが出てきましたか？' },
  { key: 'next_step', label: '次のアクション', q: '次は何に取り組みますか？' },
  { key: 'rationale', label: '技術的根拠', q: 'なぜこのアプローチを選びましたか？', hint: '意思決定の記録として将来のナレッジになります', secondary: true },
  { key: 'lessons', label: '教訓・学び', q: '将来のエンジニアに伝えたいことは？', optional: true, hint: '技術的知見・設計判断など — 将来のAI検索で活用されます', secondary: true },
  { key: 'result', label: '成果', q: '今日確認できた成果は？（予定・仮説は含めない）', optional: true, secondary: true },
  { key: 'blockers', label: 'ブロッカー', q: '進捗を止めているものはありますか？', optional: true, secondary: true },
];

const todayStr = () => new Date().toLocaleDateString('sv-SE');

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch (_e) { return iso; }
};

export default function EngReportForm() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState({ projects: [], departments: [], custom_titles: [] });
  const [form, setForm] = useState({
    report_date: todayStr(),
    project_id: '', custom_title_id: '', project_title_type: '', display_title: '',
    status: 'in_progress', attachments: [],
    objective: '', problem: '', actions: '', rationale: '', result: '', blockers: '', next_step: '', lessons: '',
  });
  const [report, setReport] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draftBanner, setDraftBanner] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiNotes, setAiNotes] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiCanSeeOthers, setAiCanSeeOthers] = useState(false);
  const [aiSel, setAiSel] = useState(null);       // ModelPresetPicker からの選択状態
  const [aiStage, setAiStage] = useState(null);   // サーバーの実処理ステージ
  const [trOpen, setTrOpen] = useState(false);
  const [trBusy, setTrBusy] = useState(false);
  const [trData, setTrData] = useState(null);
  const idRef = useRef(rid || null);
  // 下書きセッションID — 日報が未保存でも選択を安定して保持する（リロード耐性）
  const draftSessionRef = useRef(null);
  if (!draftSessionRef.current) {
    let dsid = sessionStorage.getItem('engReportDraftSession');
    if (!dsid) {
      dsid = `draft-${window.crypto?.randomUUID?.() || Date.now()}`;
      sessionStorage.setItem('engReportDraftSession', dsid);
    }
    draftSessionRef.current = dsid;
  }
  const dirtyRef = useRef(false);
  const formRef = useRef(form);
  formRef.current = form;
  const fileInputRef = useRef(null);

  const isOwner = !report || report.engineer_id === user?.id;
  const isAdminEditingOthers = !!report && !isOwner && user?.role === 'admin';
  const readOnly = !!report && !isOwner && !isAdminEditingOthers;
  const [dmsLinked, setDmsLinked] = useState(false);
  const [targetOpen, setTargetOpen] = useState(false);
  const [fieldBusy, setFieldBusy] = useState(null);
  const [suggestion, setSuggestion] = useState(null);   // {field, text, model_name, preset_name}
  const [aiPreview, setAiPreview] = useState(null);     // 全体生成のプレビュー {key: text}
  const [advOpen, setAdvOpen] = useState(false);        // Engineering Notes（詳細）
  const [aiTarget, setAiTarget] = useState('full');     // AIワークスペースの生成対象
  const [recentTargets, setRecentTargets] = useState([]);

  const openAiFor = (key) => {
    setAiTarget(key || 'full');
    setSuggestion(null);
    setAiOpen(true);
  };

  const runAiForTarget = () => (aiTarget === 'full' ? runAi() : suggestField(aiTarget));

  useEffect(() => {
    if (!targetOpen || recentTargets.length) return;
    engApi.reports({ engineer_id: user?.id, limit: 25 })
      .then((rows) => {
        const seen = new Set();
        const out = [];
        (rows || []).forEach((r) => {
          const id = r.project_id || r.custom_title_id;
          const label = r.project_name || r.display_title;
          if (!id || !label || seen.has(id)) return;
          seen.add(id);
          out.push({ type: r.project_id ? 'project' : 'title', id, label });
        });
        setRecentTargets(out.slice(0, 6));
      })
      .catch(() => {});
  }, [targetOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderField = ({ key, label, q, required, optional, hint, secondary }) => (
            <div key={key} data-testid={`report-field-${key}`}
              className={`rounded-xl border p-3.5 transition-colors ${
                secondary ? 'border-zinc-200/70 bg-zinc-50/40' : 'border-zinc-200 bg-white'}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <label className="text-sm font-medium text-zinc-900 flex items-center gap-1.5">
                    {label}
                    {required && <span className="text-xs px-1 rounded bg-rose-50 text-rose-600 border border-rose-200">必須</span>}
                    {optional && <span className="text-xs px-1 rounded bg-zinc-100 text-zinc-500 border border-zinc-200">任意</span>}
                  </label>
                  <p className="text-xs text-zinc-500 mt-0.5">{q}{hint ? ` — ${hint}` : ''}</p>
                </div>
                {!readOnly && (
                  <button type="button" onClick={() => openAiFor(key)}
                    title={`AIアシスタントを「${label}」で開く`}
                    className="shrink-0 p-1.5 rounded-lg text-zinc-300 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                    data-testid={`report-ai-field-${key}`}>
                    <Sparkle size={14} weight={aiTarget === key && aiOpen ? 'fill' : 'regular'} />
                  </button>
                )}
              </div>
              <Textarea value={form[key]} disabled={readOnly}
                onChange={(e) => set(key, e.target.value)}
                rows={Math.max(2, Math.min(14, String(form[key] || '').split('\n').length + 1))}
                className="mt-2 text-sm resize-y min-h-[52px]" data-testid={`report-input-${key}`} />
            </div>
  );

  const insertPreview = (key) => {
    const text = (aiPreview?.[key] || '').trim();
    if (!text) return;
    dirtyRef.current = true;
    set(key, text);
    setAiPreview((p) => ({ ...p, [key]: '' }));
    toast.success('日報に反映しました');
  };

  const insertPreviewAll = () => {
    if (!aiPreview) return;
    dirtyRef.current = true;
    setForm((f) => {
      const next = { ...f };
      FIELDS.forEach(({ key }) => { if ((aiPreview[key] || '').trim()) next[key] = aiPreview[key]; });
      return next;
    });
    setAiPreview(null);
    toast.success('全項目を日報に反映しました');
  };

  // ---- AI生成履歴の操作（表示 / 再利用 / 復元 / 削除）----------------------
  const loadAiHistory = () => engApi.reportAiPrompts(rid || idRef.current || undefined)
    .then((r) => { setAiHistory(r.prompts || []); setAiCanSeeOthers(!!r.can_see_others); })
    .catch(() => {});

  /** 表示 — 生成結果をパネル上部のプレビューに読み込む（日報は変更しない） */
  const showHistoryResult = (snap) => {
    const entries = Object.entries(snap || {}).filter(([, v]) => String(v || '').trim());
    if (!entries.length) { toast.error('この履歴には生成結果が保存されていません'); return; }
    setAiPreview(Object.fromEntries(entries));
    toast.success('生成結果をプレビューに読み込みました');
  };

  /** 再利用 — 元メモと生成対象をジェネレーターに戻す */
  const reuseHistory = (h) => {
    const notes = (h?.notes || '').trim();
    if (!notes) { toast.error('この履歴にはメモが保存されていません'); return; }
    setAiNotes(notes);
    const field = h.scope && h.scope.startsWith('field:') ? h.scope.slice(6) : 'full';
    setAiTarget(FIELDS.some((f) => f.key === field) ? field : 'full');
    setSuggestion(null);
    toast.success('メモを再利用しました — 内容を確認して生成できます');
  };

  /** 復元 — 生成結果を日報の該当項目に書き戻す */
  const restoreHistoryResult = (snap) => {
    const entries = Object.entries(snap || {})
      .filter(([k, v]) => FIELDS.some((f) => f.key === k) && String(v || '').trim());
    if (!entries.length) { toast.error('復元できる生成結果がありません'); return; }
    if (!window.confirm(`${entries.length}項目を生成結果で置き換えます。よろしいですか？`)) return;
    dirtyRef.current = true;
    setForm((f) => {
      const next = { ...f };
      entries.forEach(([k, v]) => { next[k] = String(v); });
      return next;
    });
    toast.success(`${entries.length}項目を日報に復元しました`);
  };

  /** 削除 — 履歴を1件削除 */
  const deleteHistory = async (h) => {
    if (!window.confirm('この生成履歴を削除します。よろしいですか？')) return;
    try {
      await engApi.reportAiPromptDelete(h.id);
      setAiHistory((rows) => rows.filter((r) => r.id !== h.id));
      toast.success('履歴を削除しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '履歴の削除に失敗しました');
    }
  };

  // The whole project row, so the badge can read the canonical `is_finished`
  // instead of the deprecated `status` string.
  const pickedProject = useMemo(
    () => (meta.projects_all || meta.projects || []).find((p) => p.id === form.project_id),
    [meta, form.project_id],
  );

  const pickTarget = (v) => {
    dirtyRef.current = true;
    if (v.type === 'project') {
      setForm((f) => ({ ...f, project_id: v.id, custom_title_id: '', project_title_type: 'project', display_title: v.label }));
    } else {
      setForm((f) => ({ ...f, project_id: '', custom_title_id: v.id, project_title_type: 'title', display_title: v.label }));
    }
  };

  const createTitle = async (title) => {
    try {
      const t = await engApi.createCustomTitle(title);
      const m = await engApi.reportsMeta();
      setMeta(m);
      pickTarget({ type: 'title', id: t.id, label: t.title });
      toast.success(`一般作業「${t.title}」を作成しました`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '作業名の作成に失敗しました');
    }
  };

  const suggestField = async (field) => {
    setFieldBusy(field);
    setSuggestion(null);
    try {
      const r = await engApi.reportAiSuggestField({
        field,
        notes: aiNotes || null,
        report_id: rid || idRef.current || null,
        draft_session_id: rid || idRef.current ? null : draftSessionRef.current,
        project_name: formRef.current.display_title || null,
        current: Object.fromEntries(FIELDS.map(({ key }) => [key, formRef.current[key]])),
        model_id: aiSel?.modelId || null,
        preset_id: aiSel?.presetId || null,
      });
      setSuggestion(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'AI提案の生成に失敗しました');
    } finally { setFieldBusy(null); }
  };

  const applySuggestion = (mode) => {
    if (!suggestion) return;
    const { field, text } = suggestion;
    const cur = (formRef.current[field] || '').trim();
    set(field, mode === 'replace' || !cur ? text : `${cur}\n${text}`);
    setSuggestion(null);
    toast.success(mode === 'replace' ? 'AI提案で置き換えました' : 'AI提案を挿入しました');
  };

  useEffect(() => {
    dmsApi.linked('eng_report', rid || 'probe')
      .then((r) => setDmsLinked(!!r?.enabled))
      .catch(() => setDmsLinked(false));
  }, [rid]);

  useEffect(() => { engApi.reportsMeta().then(setMeta).catch(() => {}); }, []);

  useEffect(() => {
    if (rid) {
      engApi.report(rid).then((r) => {
        setReport(r); idRef.current = r.id;
        setForm({
          report_date: r.report_date,
          project_id: r.project_id || '', custom_title_id: r.custom_title_id || '',
          project_title_type: r.project_title_type || (r.project_id ? 'project' : ''),
          display_title: r.display_title || r.project_name || '',
          status: r.status,
          attachments: r.attachments || [],
          objective: r.objective || '', problem: r.problem || '', actions: r.actions || '',
          rationale: r.rationale || '', result: r.result || '', blockers: r.blockers || '',
          next_step: r.next_step || '', lessons: r.lessons || '',
        });
      }).catch((e) => {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || '日報の読み込みに失敗しました');
        navigate('/engineering/reports');
      });
    } else {
      engApi.myReportDraft().then(({ draft }) => { if (draft) setDraftBanner(draft); }).catch(() => {});
    }
  }, [rid]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = useCallback(async (silent = true) => {
    if (readOnly) return null;
    const body = {
      ...formRef.current,
      project_id: formRef.current.project_id || null,
      custom_title_id: formRef.current.custom_title_id || null,
      project_title_type: formRef.current.project_title_type || null,
      display_title: formRef.current.display_title || null,
      draft_session_id: idRef.current ? null : draftSessionRef.current,
    };
    setSaving(true);
    try {
      let r;
      if (idRef.current) {
        r = await engApi.updateReport(idRef.current, body);
      } else {
        r = await engApi.createReport(body);
        idRef.current = r.id;
        window.history.replaceState(null, '', `/engineering/reports/${r.id}`);
      }
      setReport(r);
      dirtyRef.current = false;
      setSavedAt(new Date());
      if (!silent) toast.success('下書きを保存しました');
      return r;
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || '保存に失敗しました');
      return null;
    } finally {
      setSaving(false);
    }
  }, [readOnly]);

  // 自動保存: 30秒ごと + 変更3秒後
  useEffect(() => {
    const iv = setInterval(() => { if (dirtyRef.current) persist(); }, 30000);
    return () => clearInterval(iv);
  }, [persist]);
  useEffect(() => {
    if (!dirtyRef.current) return undefined;
    const t = setTimeout(() => { if (dirtyRef.current) persist(); }, 3000);
    return () => clearTimeout(t);
  }, [form, persist]);

  const set = (k, v) => { dirtyRef.current = true; setForm((f) => ({ ...f, [k]: v })); };

  const submit = async () => {
    const ptype = formRef.current.project_title_type;
    const hasTarget = (ptype === 'project' && formRef.current.project_id)
                    || (ptype === 'title' && formRef.current.custom_title_id);
    if (!hasTarget) {
      toast.error('プロジェクト / タイトルは必須です。プロジェクトを選択するか、新しいタイトルを作成してください。');
      return;
    }
    const r = await persist(true);
    if (!r) { toast.error('保存に失敗しました'); return; }
    try {
      const s = await engApi.submitReport(idRef.current);
      setReport(s);
      toast.success('日報を提出しました');
      navigate('/engineering/reports');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '提出に失敗しました');
    }
  };

  const onUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const up = await engApi.uploadReportFile(file);
        set('attachments', [...formRef.current.attachments, up]);
      }
      toast.success('ファイルを添付しました');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'アップロードに失敗しました');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const runAi = async () => {
    if (!aiNotes.trim()) return;
    if (aiSel && aiSel.selectable && !aiSel.ready) {
      toast.error('現在利用可能なテキスト生成モデルがありません。AI管理者にモデルの読み込み状況を確認してください。');
      return;
    }
    setAiBusy(true);
    setAiStage(null);
    try {
      const projName = meta.projects.find((p) => p.id === form.project_id)?.name;
      const { fields } = await engApi.reportAiAssist({
        notes: aiNotes, project_name: projName || null,
        report_id: rid || idRef.current || null,
        draft_session_id: rid || idRef.current ? null : draftSessionRef.current,
        model_id: aiSel?.modelId || null,
        preset_id: aiSel?.presetId || null,
      }, setAiStage);
      dirtyRef.current = true;
      setAiPreview(fields);
      toast.success('AI下書きを生成しました — プレビューを確認して日報に反映してください');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'AI生成に失敗しました');
    } finally {
      setAiBusy(false);
      setAiStage(null);
    }
  };

  const removeAttachment = (aid) => set('attachments', form.attachments.filter((a) => a.id !== aid));

  // AIプロンプト履歴 — ダイアログを開くたびに直近分を取得
  useEffect(() => {
    if (!aiOpen) return;
    loadAiHistory();
  }, [aiOpen, aiBusy, fieldBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  const doTranslate = async (force) => {
    const id = rid || idRef.current;
    if (!id) return;
    setTrBusy(true);
    try {
      const r = await engApi.reportTranslate(id, force);
      setTrData(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '翻訳に失敗しました');
    } finally { setTrBusy(false); }
  };

  const openTranslate = () => {
    setTrOpen(true);
    if (!trData) doTranslate(false);
  };

  const doDelete = async () => {
    const id = idRef.current;
    if (!id) { navigate('/engineering/reports'); return; }
    if (!window.confirm(`この日報を削除しますか？（${form.report_date}）\nこの操作は取り消せません。`)) return;
    try {
      await engApi.deleteReport(id);
      toast.success('日報を削除しました');
      navigate('/engineering/reports');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '削除に失敗しました');
    }
  };

  const doPrint = () => {
    const snapshot = {
      ...form,
      report_status: report?.report_status || 'draft',
      project_name: meta.projects.find((p) => p.id === form.project_id)?.name || '',
      department_name: deptName,
      engineer_name: report?.engineer_name || user?.name || '',
      submitted_at: report?.submitted_at || '',
    };
    openPrintWindow(buildReportPrintHtml(snapshot));
  };

  const deptName = useMemo(() => {
    const p = meta.projects.find((x) => x.id === form.project_id);
    return meta.departments.find((d) => d.id === p?.department_id)?.name || '';
  }, [meta, form.project_id]);

  const docState = report?.report_status || 'draft';

  return (
    <div className="max-w-3xl space-y-4" data-testid="eng-report-form-page">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" asChild className="h-8 -ml-2 text-zinc-500">
          <Link to="/engineering/reports" data-testid="report-back-link"><ArrowLeft size={14} className="mr-1" /> 一覧へ戻る</Link>
        </Button>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${REPORT_DOC_BADGE[docState]}`} data-testid="report-doc-state">
          {REPORT_DOC_LABEL[docState]}
        </span>
        {report && (
          <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-full px-2 py-0.5">
            <span className="w-4 h-4 rounded-full bg-teal-500 text-white text-[9px] font-bold flex items-center justify-center">{(report.engineer_name || '?').charAt(0)}</span>
            {report.engineer_name}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          <span className="text-xs text-zinc-400 hidden md:inline" data-testid="report-autosave-state">
            {saving ? '保存中…' : savedAt ? `自動保存済み ${formatTime(savedAt)}` : ''}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-zinc-500" data-testid="report-more-menu">
                <DotsThree size={18} weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={doPrint} data-testid="report-print-btn">
                <Printer size={14} className="mr-2 text-zinc-500" /> 印刷 / PDF
              </DropdownMenuItem>
              {!!(rid || idRef.current) && (
                <DropdownMenuItem onClick={openTranslate} data-testid="report-translate-btn">
                  <Translate size={14} className="mr-2 text-zinc-500" /> EN 英訳プレビュー
                </DropdownMenuItem>
              )}
              {idRef.current && isOwner && (
                <DropdownMenuItem onClick={doDelete} className="text-rose-600" data-testid="report-delete-btn">
                  <Trash size={14} className="mr-2" /> 日報を削除
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </div>

      {draftBanner && !rid && !idRef.current && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex flex-wrap items-center gap-2 text-[13px] text-amber-800" data-testid="report-draft-banner">
          <span>未提出の下書きがあります（{draftBanner.report_date}）</span>
          <Button size="sm" variant="outline" className="h-7 text-xs ml-auto border-amber-300"
            onClick={() => navigate(`/engineering/reports/${draftBanner.id}`)} data-testid="report-draft-resume-btn">
            続きを書く
          </Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5 space-y-5">
        <div className="flex items-start justify-between gap-3 pb-3 border-b border-zinc-200">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
              <Wrench size={20} weight="duotone" className="text-teal-600" />
              {readOnly ? '日報の閲覧' : rid || idRef.current ? '日報の編集' : '日報の作成'}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">今日の作業を書き残す — 将来のトラブルシューティング検索に使われます</p>
            {isAdminEditingOthers && (
              <p className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-[11px] font-semibold text-amber-800"
                data-testid="report-admin-edit-banner">
                管理者として {report?.engineer_name} さんの日報を編集しています
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!readOnly && (
              <Button size="sm" variant="outline" onClick={() => openAiFor('full')}
                className="h-9 border-zinc-200 text-zinc-700 hover:bg-zinc-50" data-testid="report-ai-assist-btn">
                <Sparkle size={15} weight="duotone" className="mr-1.5 text-teal-600" /> AIアシスタント
              </Button>
            )}
          </div>
        </div>

        <div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider">日付</label>
              <Input type="date" value={form.report_date} disabled={readOnly}
                onChange={(e) => set('report_date', e.target.value)} className="h-10" data-testid="report-date-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                作業対象 <span className="text-rose-500">＊</span>
              </label>
              <div className="rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 flex items-center gap-3"
                data-testid="report-work-target">
                {form.display_title ? (
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-medium text-zinc-900 truncate">{form.display_title}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-zinc-500">
                      <span className="text-zinc-400">
                        {form.project_title_type === 'project' ? 'プロジェクト' : '一般作業'}
                      </span>
                      {form.project_title_type === 'project' && projectStatusBadge(pickedProject) && (
                        <span className={`px-1.5 py-0.5 rounded border ${projectStatusBadge(pickedProject)[1]}`}
                          title="プロジェクトの状態は Planner が管理します（日報からは変更できません）"
                          data-testid="report-project-status-badge">
                          進捗: {projectStatusBadge(pickedProject)[0]}
                        </span>
                      )}
                      {deptName && <span>部署: {deptName}</span>}
                    </p>
                  </div>
                ) : (
                  <p className="flex-1 text-sm text-zinc-400">未選択 — 今日の作業対象を選んでください</p>
                )}
                {!readOnly && (
                  <Button variant="outline" size="sm" onClick={() => setTargetOpen(true)}
                    className="h-8 text-xs shrink-0" data-testid="report-work-target-change">変更</Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {FIELDS.filter((f) => !f.secondary).map(renderField)}

          <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 overflow-hidden">
            <button type="button" onClick={() => setAdvOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-3.5 py-3 text-left hover:bg-zinc-100/60 transition-colors"
              data-testid="report-advanced-toggle">
              <CaretDown size={13} className={`text-zinc-400 transition-transform ${advOpen ? '' : '-rotate-90'}`} />
              <span className="text-sm font-medium text-zinc-800">エンジニアリングノート</span>
              <span className="text-xs text-zinc-400 hidden sm:inline">技術的根拠・教訓・成果・ブロッカー</span>
              {!advOpen && FIELDS.filter((f) => f.secondary).some((f) => (form[f.key] || '').trim()) && (
                <span className="ml-auto text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5">入力あり</span>
              )}
            </button>
            {advOpen && (
              <div className="px-3 pb-3 space-y-3" data-testid="report-advanced-body">
                {FIELDS.filter((f) => f.secondary).map(renderField)}
              </div>
            )}
          </div>
        </div>

        {(!dmsLinked || form.attachments.length > 0) && (
        <div>
          <label className="text-[13px] font-semibold text-zinc-800 flex items-center gap-1.5">
            <Paperclip size={14} /> {dmsLinked ? '旧添付ファイル' : '添付ファイル'}
          </label>
          {!dmsLinked && <p className="text-[11px] text-zinc-400 mb-1.5">画像・PDF・文書（25MBまで）</p>}
          {form.attachments.length > 0 && (
            <ul className="space-y-1.5 mb-2">
              {form.attachments.map((a) => (
                <li key={a.id} className="flex items-center gap-2 text-[13px] bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-1.5" data-testid={`report-attachment-${a.id}`}>
                  {a.is_pdf ? <FilePdf size={15} className="text-rose-500 shrink-0" />
                    : a.is_image ? <ImageIcon size={15} className="text-sky-500 shrink-0" />
                      : <FileIcon size={15} className="text-zinc-400 shrink-0" />}
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${a.url.startsWith('/api') ? a.url : `/api${a.url}`}`}
                    target="_blank" rel="noreferrer" className="truncate text-teal-700 hover:underline">{a.filename}</a>
                  <span className="text-[10px] text-zinc-400 shrink-0">{(a.size / 1024).toFixed(0)} KB</span>
                  {!readOnly && !dmsLinked && (
                    <button type="button" onClick={() => removeAttachment(a.id)} className="ml-auto text-zinc-400 hover:text-rose-500" data-testid={`report-attachment-remove-${a.id}`}>
                      <Trash size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {!readOnly && !dmsLinked && (
            <>
              <input ref={fileInputRef} type="file" multiple className="hidden"
                accept=".png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                onChange={(e) => onUpload(e.target.files)} data-testid="report-file-input" />
              <Button size="sm" variant="outline" className="h-8" disabled={uploading}
                onClick={() => fileInputRef.current?.click()} data-testid="report-upload-btn">
                <Paperclip size={13} className="mr-1" /> {uploading ? 'アップロード中…' : 'ファイルを添付'}
              </Button>
            </>
          )}
        </div>
        )}

        {dmsLinked && (
          (rid || idRef.current)
            ? <LinkedDocsPanel targetType="eng_report" targetId={rid || idRef.current} readOnly={readOnly} />
            : <p className="text-[11px] text-zinc-400">関連書類は下書き保存後に添付できます</p>
        )}

        {(rid || idRef.current) && (
          <TraceabilityPanel entityType="daily_report" entityId={rid || idRef.current} className="pt-3 border-t border-zinc-200" />
        )}
      </div>

      {!readOnly && (
        <div className="sticky bottom-3 bg-white/95 backdrop-blur rounded-xl border border-zinc-200 shadow-lg px-4 py-3 flex items-center gap-2">
          <p className="text-xs text-zinc-400 hidden sm:block">30秒ごとに自動保存されます</p>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => persist(false)} disabled={saving} className="h-9 text-sm" data-testid="report-save-draft-btn">
              <FloppyDisk size={14} className="mr-1.5" /> 下書き保存
            </Button>
            {docState === 'draft' ? (
              <Button onClick={submit} disabled={saving} className="h-9 px-5 text-sm bg-teal-700 hover:bg-teal-800" data-testid="report-submit-btn">
                <PaperPlaneTilt size={15} className="mr-1.5" /> 日報を提出
              </Button>
            ) : (
              <span className="inline-flex items-center gap-1 text-sm text-emerald-600 font-medium">
                <CheckCircle size={15} weight="fill" /> 提出済み（編集は自動保存されます）
              </span>
            )}
          </div>
        </div>
      )}

      <EngWorkTargetDialog open={targetOpen} onClose={() => setTargetOpen(false)}
        meta={meta} onPick={pickTarget} onCreateTitle={createTitle} recent={recentTargets} />

      <EngReportAiPanel
        open={aiOpen} onClose={() => setAiOpen(false)}
        fields={FIELDS} target={aiTarget} setTarget={(k) => { setAiTarget(k); setSuggestion(null); }}
        notes={aiNotes} setNotes={setAiNotes}
        aiSel={aiSel} setAiSel={setAiSel}
        busy={aiBusy || !!fieldBusy} stage={aiStage}
        instanceId={rid || idRef.current || draftSessionRef.current}
        onGenerate={runAiForTarget}
        preview={aiPreview} onInsertPreview={insertPreview} onInsertPreviewAll={insertPreviewAll}
        suggestion={suggestion} onApplySuggestion={applySuggestion}
        onDiscardSuggestion={() => setSuggestion(null)}
        history={aiHistory} canSeeOthers={aiCanSeeOthers}
        onReuseNotes={reuseHistory} onShowResult={showHistoryResult}
        onRestoreResult={restoreHistoryResult} onDeleteHistory={deleteHistory} />


      {/* 英訳プレビュー — English readers can review the entry */}
      <Dialog open={trOpen} onOpenChange={setTrOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="report-translate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Translate size={18} weight="duotone" className="text-sky-600" /> English Preview（英訳プレビュー）
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11.5px] text-zinc-500">
            保存済みの日報内容をAIが英訳します。結果はこの日報に保存され、本文を更新すると再翻訳できます。
          </p>
          <AiProgressBar busy={trBusy} stages={['本文を翻訳中…', '専門用語を確認中…（ローカルLLMでは時間がかかる場合があります）']} />
          {!trBusy && trData?.translation && (
            <div className="space-y-3" data-testid="report-translate-result">
              {trData.translation.fields.display_title && (
                <p className="text-[14px] font-bold text-zinc-900">{trData.translation.fields.display_title}</p>
              )}
              {FIELDS.map(({ key, label }) => {
                const en = trData.translation.fields[key];
                if (!en) return null;
                return (
                  <div key={key} className="rounded-lg border border-zinc-200 bg-white p-3">
                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1">{label}</p>
                    <p className="text-[12.5px] text-zinc-800 leading-relaxed whitespace-pre-wrap">{en}</p>
                  </div>
                );
              })}
              <p className="text-[10.5px] text-zinc-400">
                {trData.cached ? 'キャッシュ済みの翻訳' : '翻訳を生成しました'} · {fmtWhen(trData.translation.generated_at)}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrOpen(false)} disabled={trBusy}>閉じる</Button>
            <Button onClick={() => doTranslate(true)} disabled={trBusy}
              className="bg-teal-700 hover:bg-teal-800" data-testid="report-translate-regenerate-btn">
              <ArrowsClockwise size={14} className="mr-1" /> 再翻訳
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
